import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useRetrievePlanzoneResourcesListing,
  useRetrievePlanzoneResourcesCounts,
  useRetrieveCurrentPlanzone,
  useRetrieveResourceGroups,
  useExportData,
  useAuthentication,
} from "@planzone/view-models";
import { UseCases } from "@planzone/core";
import {
  ExportsDataTypes,
  ExportsFormats,
  ResourcesListingFilter,
  ResourcesListingSort,
} from "@planzone/dto";

import { usePreferences } from "@components/preferences";
import { WorkspaceContext } from "@scenes/workspaceContext";
import { Safeguard } from "@scenes/safeguard";
import { Notifications } from "@scenes/notifications";
import { AssistantShortcut } from "@scenes/assistantShortcut";
import { ProfileNavigation } from "@common/profileNavigation";
import { Loader } from "@common/loader";
import { DragAndDrop } from "@common/dragAndDrop";
import { CogWheelIcon } from "@common/cogWheelIcon";
import { useModal } from "@root/client/modal";
import { Menu } from "./menu";
import { ResourcesList } from "./resourcesList";
import { ResourcesMenu } from "./resourcesMenu";
import { GroupsList } from "./groupsList";
import { Toolbar } from "./toolbar";
import { ToggleViewButton } from "./toggleViewButton";
import { Props, SortOption, Filter } from "./types";
import { ExportButton } from "./exportButton";
import { useResourcesListingColumns } from "./columns";
import styles from "./styles.scss";
import classNames from "classnames";

const PAGE_SIZE = 50;

const listingSorts: { [Key in SortOption]: ResourcesListingSort } = {
  [SortOption.Default]: ResourcesListingSort.Default,
  [SortOption.NAME]: ResourcesListingSort.Name,
  [SortOption.Role]: ResourcesListingSort.Role,
  [SortOption.CreationDate]: ResourcesListingSort.CreationDate,
};

function PlanzoneMembersPage(_: Props) {
  const { account } = useAuthentication();
  const { exportData } = useExportData();
  const { t } = useTranslation();
  const { state, edit } = usePreferences();
  const { resourcesFilter: filter } = state;
  const setFilter = (nextFilter: Filter) =>
    edit({ ...state, resourcesFilter: nextFilter });
  const { planzone } = useRetrieveCurrentPlanzone();
  const hasPlanzone = !!planzone.uuid;
  const { groups, isRequestPending: isRequestGroupPending } =
    useRetrieveResourceGroups(planzone.uuid);
  const {
    resources: listedResources,
    filter: listingFilter,
    totalPages,
    isRequestPending: isListingRequestPending,
    retrievePlanzoneResourcesListing,
  } = useRetrievePlanzoneResourcesListing();
  const { counts, retrievePlanzoneResourcesCounts } =
    useRetrievePlanzoneResourcesCounts();
  const { columns, label } = useResourcesListingColumns();
  const { pushView } = useModal();
  const [sort, setSort] = useState<SortOption>(SortOption.Default);
  const listingCriteria: ResourcesListingFilter = {
    page: 0,
    size: PAGE_SIZE,
    resourceTypes: filter.resourceTypes,
    roles: filter.roles,
    inactiveOnly: filter.inactiveOnly,
    activeOnly: filter.activeOnly,
    organizationalUnits: filter.organizationalUnits,
    resources: filter.resources,
    groups: filter.groups,
    resourceTags: filter.resourceTags,
    sort: listingSorts[sort],
  };

  React.useEffect(() => {
    hasPlanzone && retrievePlanzoneResourcesCounts({ planzone });
  }, [planzone.uuid]);

  React.useEffect(() => {
    hasPlanzone &&
      retrievePlanzoneResourcesListing({ planzone, filter: listingCriteria });
  }, [planzone.uuid, JSON.stringify(listingCriteria)]);

  return (
    <WorkspaceContext type="planzone">
      <DragAndDrop>
        <div className={styles.planzoneMembersPage}>
          <div>
            <div className={styles.header}>
              <h1 className={styles.title}>{t("planzoneMembersPage.title")}</h1>
              <div className={styles.profileNavigation}>
                <AssistantShortcut />
                <Notifications />
                <ProfileNavigation />
              </div>
            </div>
            <div className={styles.menu}>
              <Toolbar
                sort={sort}
                filter={filter}
                setSort={setSort}
                setFilter={setFilter}
              />
              <div className={styles.menuRight}>
                <div className={styles.toggleViewButton}>
                  <p>{t("planzoneMembersPage.toggleView")}</p>
                  <ToggleViewButton
                    areSortedByGroups={filter.areSortedByGroups}
                    setAreSortedByGroup={() =>
                      setFilter({
                        ...filter,
                        areSortedByGroups: !filter.areSortedByGroups,
                      })
                    }
                  />
                </div>
                <Menu />
              </div>
            </div>
          </div>
          <div className={styles.main}>
            <div className={styles.sidebar}>
              <ResourcesMenu
                counts={counts}
                filters={filter}
                setFilters={setFilter}
              />
            </div>
            <div
              className={classNames(styles.content, {
                [styles.withGroups]: filter.areSortedByGroups,
              })}
            >
              {filter.areSortedByGroups ? (
                <GroupsList
                  groups={groups}
                  filter={filter}
                  sort={sort}
                  isRequestPending={isRequestGroupPending}
                  columns={columns}
                />
              ) : (
                <React.Fragment>
                  <div className={styles.heading}>
                    <p className={styles.label}>
                      {t("planzoneMembersPage.resources.name")}
                    </p>
                    {columns.map(({ key }) => (
                      <p key={key} className={styles.label}>
                        {label(key)}
                      </p>
                    ))}
                    <Safeguard
                      useCases={[UseCases.editResourcesListingConfiguration]}
                    >
                      {({ canEditResourcesListingConfiguration }) => (
                        <div
                          className={classNames(styles.label, styles.cogWheel)}
                        >
                          {canEditResourcesListingConfiguration && (
                            <div
                              onClick={() =>
                                pushView({
                                  key: UseCases.retrieveResourcesListingConfiguration,
                                  data: undefined,
                                })
                              }
                            >
                              <CogWheelIcon className={styles.cogIcon} />
                            </div>
                          )}
                        </div>
                      )}
                    </Safeguard>
                  </div>
                  <div className={styles.resourcesList}>
                    {isListingRequestPending && listedResources.length === 0 ? (
                      <div className={styles.loader}>
                        <div className={styles.loaderMain}>
                          <Loader />
                        </div>
                      </div>
                    ) : (
                      <ResourcesList
                        resources={listedResources}
                        columns={columns}
                        isRequestPending={isListingRequestPending}
                        canLoadMore={listingFilter.page < totalPages}
                        retrieveNextResources={() =>
                          retrievePlanzoneResourcesListing({
                            planzone,
                            filter: listingFilter,
                          })
                        }
                      />
                    )}
                  </div>
                </React.Fragment>
              )}
            </div>
          </div>
        </div>
      </DragAndDrop>
      <div className={styles.exportButton}>
        <ExportButton
          export={() => {
            exportData({
              fileName: t("planzoneMembersPage.workbookName") + planzone.name,
              author: account,
              data: {
                type: ExportsDataTypes.RESOURCES_REPORT,
                data: {
                  planzone,
                  filter: listingCriteria,
                },
              },
              format: ExportsFormats.EXCEL,
            });
          }}
        ></ExportButton>
      </div>
    </WorkspaceContext>
  );
}

export { PlanzoneMembersPage };
